import { cva, type VariantProps } from 'class-variance-authority';
import { ComponentProps, forwardRef } from 'react';
import { cn } from '../utils/className';

const numberInputVariants = cva(
  [
    'rounded',
    'border',
    'font-regular',
    'transition-colors',
    'outline-none',
    'focus-within:border-border-focus',
    // Hide number input arrows
    '[appearance:textfield]',
    '[&::-webkit-outer-spin-button]:appearance-none',
    '[&::-webkit-inner-spin-button]:appearance-none',
  ],
  {
    variants: {
      variant: {
        default: [
          'bg-surface-primary',
          'border-border-primary',
          'text-primary',
          'placeholder:text-disabled',
          'hover:bg-blue-050',
          'hover:border-border-focus',
          'focus:border-border-focus',
          'focus:bg-surface-primary',
          'focus:text-focus',
        ],
        inactive: [
          'bg-surface-primary',
          'border-border-primary',
          'text-secondary',
          'placeholder:text-tertiary',
          'hover:bg-blue-050',
          'hover:border-border-focus',
          'focus:border-border-focus',
          'focus:bg-surface-primary',
          'focus:text-focus',
        ],
        error: [
          'bg-surface-negative-support',
          'border-border-negative',
          'text-negative',
          'placeholder:text-negative',
          'focus:border-border-negative',
        ],
        disabled: [
          'bg-surface-primary-support',
          'border-border-disabled',
          'text-disabled',
          'placeholder:text-disabled',
          'cursor-not-allowed',
        ],
      },
      size: {
        default: [
          'px-2',
          'py-1.5',
          'size-p2',
          'h-11', // 44px on mobile for touch targets
          'md:h-auto', // Auto height on desktop
        ],
        compact: [
          'px-2',
          'py-1',
          'size-p2',
          'h-8.5', // Fixed 34px height
        ],
        small: [
          'px-1.5',
          'py-0.5',
          'size-caption',
          'h-7', // 28px
        ],
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

interface NumberInputProps
  extends
    Omit<ComponentProps<'input'>, 'type' | 'size'>,
    VariantProps<typeof numberInputVariants> {
  label?: string;
  error?: boolean;
  suffix?: string;
}

export const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(
  (
    { label, error, variant, size, suffix, className, disabled, ...props },
    ref
  ) => {
    const effectiveVariant = disabled ? 'disabled' : error ? 'error' : variant;

    const InputElement = (
      <div
        className={cn(
          'relative inline-flex items-center',
          numberInputVariants({ variant: effectiveVariant, size }),
          className
        )}
      >
        <input
          ref={ref}
          type='number'
          disabled={disabled}
          className={cn(
            'w-10 border-none bg-transparent text-center outline-none',
            'placeholder:text-disabled',
            // Hide number input arrows
            '[appearance:textfield]',
            '[&::-webkit-outer-spin-button]:appearance-none',
            '[&::-webkit-inner-spin-button]:appearance-none',
            effectiveVariant === 'disabled' && 'cursor-not-allowed'
          )}
          {...props}
        />
        {suffix && (
          <span
            className={cn(
              'size-caption ml-1 whitespace-nowrap',
              effectiveVariant === 'disabled'
                ? 'text-disabled'
                : effectiveVariant === 'error'
                  ? 'text-negative'
                  : 'text-secondary'
            )}
          >
            {suffix}
          </span>
        )}
      </div>
    );

    if (label) {
      return (
        <div className='flex flex-col gap-1.5'>
          <label className='size-p2 font-regular text-primary'>{label}</label>
          {InputElement}
        </div>
      );
    }

    return InputElement;
  }
);

NumberInput.displayName = 'NumberInput';
